import { h, hydrate, render } from "preact";
import { localizePath, PProductError, RArray, Tracking, } from "restaumatic-client";
import Alert from "../../components/Alert";
import * as GroupToggle from "../../components/GroupToggle";
import FulfillmentSettings from "./FulfillmentSettings";
import SectionItemButton from "./SectionItemButton";
import SectionItemColumn from "./SectionItemColumn";
import ProductLabels from "../../components/ProductLabels";
import AIWaiterResult from "./AIWaiterResult";
export class MenuIntegration {
    constructor() {
        this.isGroupToggleInitialized = false;
    }
    showElement(element) {
        element.classList.remove("hidden");
    }
    hideElement(element) {
        element.classList.add("hidden");
    }
    initializeGroupToggle() {
        if (!this.isGroupToggleInitialized) {
            GroupToggle.init();
            this.isGroupToggleInitialized = true;
        }
    }
    /*
     * This is a performance optimization that allows the early display of the first menu section
     * if it is not hidden by `pendolino/themes/base/partials/menu_group.liquid:43` due to visibilitySchedule
     * This fix makes menu appear to load much faster in restaurants not using visibilitySchedules (approx ~94% as of Mar 2024)
     */
    tryShowFirstMenuSectionEarly() {
        const firstSectionToggleElement = window.document
            .getElementsByClassName("js-toggle-group-list-item")
            .item(0);
        if (firstSectionToggleElement !== null &&
            !firstSectionToggleElement.classList.contains("hidden")) {
            this.initializeGroupToggle();
        }
    }
    initializeElements(context, openProductCreator) {
        const sectionElements = new RArray(window.document.getElementsByClassName("menuv2-section")).mapOptional((sectionElement) => this.initializeSectionElement(context, sectionElement));
        // Important: this has to happen after hiding invisible sections, because
        // it chooses the first section to open.
        // Sometimes initialization could be performed sooner:
        // see MenuIntegration.tryEarlyShow()
        this.initializeGroupToggle();
        for (const [menuSection, sectionElement] of sectionElements) {
            this.initializeSectionContents(context, openProductCreator, menuSection, sectionElement);
        }
        // it chooses the first section to open
        GroupToggle.init();
        // Initialize alerts
        this.initializeAlerts(context);
        this.initializeAiWaiter(context, openProductCreator);
        // Initialize fulfillment settings
        this.initializeFulfillmentSettings(context);
    }
    initializeAiWaiter(context, openProductCreator) {
        const bar = window.document.getElementById("ai-search");
        const overlay = window.document.getElementById("ai-search-overlay");
        // Elements are not present if feature-flag in settings is disabled
        if (bar === null || overlay === null) {
            return;
        }
        // Check if element is visible (hidden on desktop)
        const barStyle = window.getComputedStyle(bar);
        const barIsVisible = barStyle.display !== "none" && barStyle.visibility !== "hidden";
        if (!barIsVisible) {
            return;
        }
        const form = bar.querySelector("#ai-search-form");
        const input = bar.querySelector("#ai-search-input");
        const clearBtn = bar.querySelector("#ai-search-clear");
        const closeBtn = bar.querySelector("#ai-search-close");
        if (form === null ||
            input === null ||
            closeBtn === null ||
            clearBtn === null) {
            return;
        }
        // TODO: Fix trigger event (maybe button that looks like an input?)
        // TODO: Fix keyboard navigation: focus only inside the opened overlay
        input.disabled = false;
        input.addEventListener("click", (event) => {
            // Check if already opened
            if (bar.classList.contains("is-open") &&
                !closeBtn.classList.contains("hidden")) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            input.blur();
            bar.classList.add("is-open");
            closeBtn.classList.remove("hidden");
            // NOTICE: iOS keyboard hack - needs both focus() and click() to open keyboard
            input.focus();
            input.click();
            Tracking.trackEvent({ tag: "AiWaiterOpen" });
        });
        input.addEventListener("input", () => {
            clearBtn.classList.toggle("hidden", input.value === "");
        });
        clearBtn.addEventListener("click", () => {
            input.value = "";
            input.focus();
            clearBtn.classList.add("hidden");
            Tracking.trackEvent({ tag: "AiWaiterClear" });
        });
        closeBtn.addEventListener("click", () => {
            bar.classList.remove("is-open");
            closeBtn.classList.add("hidden");
            clearBtn.classList.add("hidden");
            input.value = "";
            context.aiWaiter.reset();
            Tracking.trackEvent({ tag: "AiWaiterClose" });
        });
        form.addEventListener("submit", (event) => {
            event.preventDefault();
            context.aiWaiter.submit(input.value);
            input.blur();
        });
        setTimeout(() => {
            var _a, _b;
            const cartModalDialog = (_a = document
                .getElementById("cart")) === null || _a === void 0 ? void 0 : _a.querySelector(".modal-dialog");
            const productCardModalDialog = (_b = document
                .getElementById("product-creator")) === null || _b === void 0 ? void 0 : _b.querySelector(".modal-dialog");
            if (cartModalDialog) {
                cartModalDialog.classList.add("modal-dialog--full-height-vh");
            }
            if (productCardModalDialog) {
                productCardModalDialog.classList.add("modal-dialog--full-height-vh");
            }
        }, 500);
        // Observer for adding sticky state class to the bar
        const aiSearchObserver = new IntersectionObserver(([entry]) => {
            const stickyEl = entry.target;
            if (entry.boundingClientRect.top === 0) {
                stickyEl.classList.add("is-stuck");
            }
            else {
                stickyEl.classList.remove("is-stuck");
            }
        }, { rootMargin: "0px 0px -100% 0px" } // Detect when it touches the top
        );
        aiSearchObserver.observe(bar);
        // Render results inside the overlay
        render(h(AIWaiterResult, { aiWaiter: context.aiWaiter, 
            // onHintClick={(test: string) => {}}
            openProductCreator: openProductCreator }), overlay);
    }
    initializeAlerts(context) {
        var _a;
        const sectionElement = (_a = window.document.getElementById("alerts-wrapper")) !== null && _a !== void 0 ? _a : null;
        // If menu widget is disabled in restaurant's account settings, server doesn't render the section
        if (sectionElement === null) {
            return;
        }
        const { nextOrderingTime, orderingHoursAvailability } = context.cart;
        if (!orderingHoursAvailability.isOutOfOrderingHours) {
            return;
        }
        const error = PProductError.outOfOrderingHours(nextOrderingTime.nextOrderAvailableAt, orderingHoursAvailability.canPreorder);
        render(h(Alert, { type: "info", message: error.message, fieldId: "OrderingHoursMessage" }), sectionElement);
    }
    initializeFulfillmentSettings(context) {
        var _a;
        const sectionElement = (_a = window.document.getElementById("fulfillment-settings-wrapper")) !== null && _a !== void 0 ? _a : null;
        // If menu widget is disabled in restaurant's account settings, server doesn't render the section
        if (sectionElement === null) {
            return;
        }
        const { fulfillmentFormController, orderDetails, restaurantName, fulfillmentTime, fulfillmentMethodOptionsShort, deliveryAddressCoordinator, } = context.cart;
        if (!fulfillmentFormController.isFulfillmentEditable ||
            !orderDetails.isFulfillmentEditable) {
            sectionElement.remove();
            return;
        }
        render(h(FulfillmentSettings, { restaurantName: restaurantName, orderDetails: orderDetails, methodController: fulfillmentFormController.fulfillmentMethodController, methodOptions: fulfillmentMethodOptionsShort, fulfillmentTime: fulfillmentTime, deliveryAddressCoordinator: deliveryAddressCoordinator, isCartOpen: context.isCartOpen, onContactRestaurant: () => {
                location.href = localizePath("/kontakt");
            } }), sectionElement);
    }
    initializeSectionElement(context, sectionElement) {
        const rawMenuSectionId = sectionElement.getAttribute("data-menu-section-id");
        if (rawMenuSectionId === null) {
            return null;
        }
        const menuSection = context.findSection(rawMenuSectionId);
        if (menuSection === null || !menuSection.isVisible) {
            console.log(`Menu section ${rawMenuSectionId} not available, hiding`);
            this.initializeToggleElements(rawMenuSectionId, false);
            this.hideElement(sectionElement);
            sectionElement.classList.remove("js-group-item");
            return null;
        }
        console.log(`Menu section ${rawMenuSectionId} available,`);
        this.initializeToggleElements(rawMenuSectionId, true);
        return [menuSection, sectionElement];
    }
    initializeSectionContents(context, openProductCreator, menuSection, sectionElement) {
        for (const columnElement of sectionElement.getElementsByClassName("menuv2-column")) {
            this.initializeColumnElement(menuSection, columnElement);
        }
        for (const buttonElement of sectionElement.getElementsByClassName("menuv2-cart-button")) {
            this.initializeButtonElement(openProductCreator, menuSection, buttonElement);
        }
        for (const labelElement of sectionElement.getElementsByClassName("js-item__label")) {
            this.initializeLabelElement(context, menuSection, labelElement);
        }
    }
    initializeToggleElements(menuSectionId, toggleElementsVisible) {
        for (const toggleElement of window.document.querySelectorAll(`.js-toggle-group-list-item[data-section-id="${menuSectionId}"]`)) {
            if (toggleElementsVisible) {
                this.showElement(toggleElement);
            }
            else {
                this.hideElement(toggleElement);
            }
        }
    }
    getColumn(menuSection, columnElement) {
        const menuSectionItemId = columnElement.getAttribute("data-menu-section-item-id");
        if (menuSectionItemId === null) {
            return null;
        }
        const menuSectionItem = menuSection.items.rawFind(menuSectionItemId);
        if (menuSectionItem === null ||
            menuSectionItem.content.type !== "SingleProduct") {
            return null;
        }
        const customParameterChoiceId = columnElement.getAttribute("data-choice-id");
        if (customParameterChoiceId === null) {
            return null;
        }
        return menuSectionItem.content.columns.rawFind(customParameterChoiceId);
    }
    initializeColumnElement(menuSection, columnElement) {
        const column = this.getColumn(menuSection, columnElement);
        if (column === null) {
            this.hideElement(columnElement); // TODO Verify this behaviour
            return;
        }
        // TODO investigate innerHTML
        columnElement.innerHTML = "";
        render(h(SectionItemColumn, { column: column }), columnElement);
    }
    initializeButtonElement(openProductCreator, menuSection, buttonElement) {
        const menuSectionItemId = buttonElement.getAttribute("data-menu-section-item-id");
        const menuSectionItem = menuSectionItemId === null
            ? null
            : menuSection.items.rawFind(menuSectionItemId);
        if (menuSectionItem === null) {
            this.hideElement(buttonElement); // TODO Verify this behaviour
            return;
        }
        // TODO investigate innerHTML
        buttonElement.innerHTML = "";
        hydrate(h(SectionItemButton, { menuSectionItemContent: menuSectionItem.content, openProductCreator: (productInstance, element) => openProductCreator(productInstance, element), disableDropdown: false }), buttonElement);
    }
    initializeLabelElement(context, menuSection, labelElement) {
        const menuSectionItemId = labelElement.getAttribute("data-menu-section-item-id");
        const menuSectionItem = menuSectionItemId === null
            ? null
            : menuSection.items.rawFind(menuSectionItemId);
        if (menuSectionItem === null) {
            this.hideElement(labelElement); // TODO Verify this behaviour
            return;
        }
        const availabilityScheduleId = labelElement.getAttribute("data-availability-schedule-id");
        const availabilitySchedule = availabilityScheduleId === null
            ? null
            : context.findSchedule(availabilityScheduleId);
        const labelsCreator = context.createLabelsCreator(menuSectionItem);
        labelElement.innerHTML = "";
        render(h(ProductLabels, { labelsCreator: labelsCreator, schedule: availabilitySchedule === null ? undefined : availabilitySchedule }), labelElement);
    }
}
